import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../../utils/auth';
import { PRODUCT_MASTER_PRIVILEGES } from '../../../../utils/productMasterPrivileges';
import Sidebar from '../../../../components/Sidebar/sidebar';
import Header from '../../../../components/Header';
import Pagination from '../../../../components/Pagination/Pagination';
import './ClassofPolicyMaster.css';
import api from '../../../../utils/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
    ArchiveRestore,
    Ban,
    ChevronDown,
    Download,
    Edit,
    FolderTree,
    Hash,
    Layers3,
    ListChecks,
    Plus,
    RefreshCw,
    Search,
    ShieldCheck,
    Tags,
    X
} from 'lucide-react';
import { API_BASE_URL } from '../../../../utils/apiBase';
import { toast } from '../../../../components/notifications/notificationService';

const emptyForm = {
    positionNumber: '',
    originalPositionNumber: null,
    classOfPolicyName: '',
    categoryId: '',
    claimsTemplate: '',
    policyType: ''
};

const ClassofPolicyMaster = () => {
    const privileges = getUserPrivileges();
    const isAdmin = getUserRole() === 'admin';
    const hasPrivilege = (key) => isAdmin || privileges.includes(key);
    const classPrivileges = PRODUCT_MASTER_PRIVILEGES.classOfPolicy;
    const [collapsed, setCollapsed] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [showPopup, setShowPopup] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [editMode, setEditMode] = useState(false);
    const [currentClassId, setCurrentClassId] = useState(null);
    const [categories, setCategories] = useState([]);
    const [classOfPolicies, setClassOfPolicies] = useState([]);
    const [inactiveList, setInactiveList] = useState([]);
    const [showInactivePopup, setShowInactivePopup] = useState(false);
    const [activeSearchText, setActiveSearchText] = useState('');
    const [activeCategoryFilter, setActiveCategoryFilter] = useState('');
    const [activePolicyTypeFilter, setActivePolicyTypeFilter] = useState('');
    const [inactiveSearchText, setInactiveSearchText] = useState('');
    const [inactiveCategoryFilter, setInactiveCategoryFilter] = useState('');
    const [sortField, setSortField] = useState('positionNumber');
    const [sortOrder, setSortOrder] = useState('asc');
    const [activePage, setActivePage] = useState(1);
    const [activeItemsPerPage, setActiveItemsPerPage] = useState(10);
    const [inactivePage, setInactivePage] = useState(1);
    const [inactiveItemsPerPage, setInactiveItemsPerPage] = useState(10);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [classToDelete, setClassToDelete] = useState(null);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [positionError, setPositionError] = useState('');
    const [isPositionChecking, setIsPositionChecking] = useState(false);
    const [isPositionUnique, setIsPositionUnique] = useState(true);
    const positionCheckTimer = useRef(null);

    useEffect(() => {
        const auth = isAuthenticated();
        setAuthenticated(auth);
        setCheckingAuth(false);
    }, []);

    const showNotification = useCallback((message, type) => {
        (toast[type] || toast.info)(message);
    }, []);

    const loadActiveClasses = useCallback(async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/class-of-policies`);
            setClassOfPolicies(res.data || []);
        } catch {
            showNotification('Error refreshing active list', 'error');
        }
    }, [showNotification]);

    const loadInitialData = useCallback(async () => {
        try {
            const [catRes, activeRes] = await Promise.all([
                api.get(`${API_BASE_URL}/api/categories`),
                api.get(`${API_BASE_URL}/api/class-of-policies`)
            ]);

            setCategories(catRes.data || []);
            setClassOfPolicies(activeRes.data || []);
        } catch (error) {
            showNotification('Error loading data', 'error');
        }
    }, [showNotification]);

    useEffect(() => {
        if (!authenticated) return;
        loadInitialData();
    }, [authenticated, loadInitialData]);

    useEffect(() => () => {
        if (positionCheckTimer.current) clearTimeout(positionCheckTimer.current);
    }, []);

    const getCategoryId = (item) => (
        typeof item.categoryId === 'object' ? item.categoryId?._id : item.categoryId
    );

    const getCategoryName = useCallback((item) => {
        const category = item.categoryId;
        if (category && typeof category === 'object') return category.categoryName || '-';
        const found = categories.find((cat) => cat._id === category);
        return found?.categoryName || '-';
    }, [categories]);

    const sortedActive = useMemo(() => (
        [...classOfPolicies].sort((a, b) => Number(a.positionNumber || 0) - Number(b.positionNumber || 0))
    ), [classOfPolicies]);

    const filteredActive = useMemo(() => {
        const text = activeSearchText.trim().toLowerCase();
        return sortedActive.filter((item) => {
            const searchable = [
                item.positionNumber,
                item.classOfPolicyName,
                getCategoryName(item),
                item.claimsTemplate,
                item.policyType
            ].join(' ').toLowerCase();

            return (
                (!text || searchable.includes(text)) &&
                (!activeCategoryFilter || getCategoryId(item) === activeCategoryFilter) &&
                (!activePolicyTypeFilter || item.policyType === activePolicyTypeFilter)
            );
        });
    }, [sortedActive, activeSearchText, activeCategoryFilter, activePolicyTypeFilter, getCategoryName]);

    const filteredInactive = useMemo(() => {
        const text = inactiveSearchText.trim().toLowerCase();
        return inactiveList
            .filter((item) => {
                const searchable = [
                    item.positionNumber,
                    item.classOfPolicyName,
                    getCategoryName(item),
                    item.claimsTemplate,
                    item.policyType
                ].join(' ').toLowerCase();

                return (!text || searchable.includes(text)) && (!inactiveCategoryFilter || getCategoryId(item) === inactiveCategoryFilter);
            })
            .sort((a, b) => {
                const getVal = (item) => {
                    if (sortField === 'categoryName') return getCategoryName(item);
                    if (sortField === 'positionNumber') return Number(item.positionNumber || 0);
                    return (item[sortField] || '').toString();
                };

                const valA = getVal(a);
                const valB = getVal(b);

                if (typeof valA === 'number' && typeof valB === 'number') {
                    return sortOrder === 'asc' ? valA - valB : valB - valA;
                }

                return sortOrder === 'asc'
                    ? String(valA).localeCompare(String(valB))
                    : String(valB).localeCompare(String(valA));
            });
    }, [inactiveList, inactiveSearchText, inactiveCategoryFilter, sortField, sortOrder, getCategoryName]);

    const activeLast = activePage * activeItemsPerPage;
    const activeFirst = activeLast - activeItemsPerPage;
    const activePolicies = filteredActive.slice(activeFirst, activeLast);

    const inactiveLast = inactivePage * inactiveItemsPerPage;
    const inactiveFirst = inactiveLast - inactiveItemsPerPage;
    const inactiveItems = filteredInactive.slice(inactiveFirst, inactiveLast);

    useEffect(() => {
        setActivePage(1);
    }, [activeSearchText, activeCategoryFilter, activePolicyTypeFilter]);

    useEffect(() => {
        setInactivePage(1);
    }, [inactiveSearchText, inactiveCategoryFilter, sortField, sortOrder]);

    const summaryCards = [
        { label: 'Active Classes', value: classOfPolicies.length, note: 'Available for policy setup', icon: Layers3, tone: 'is-blue' },
        { label: 'Categories', value: categories.length, note: 'Mapped product categories', icon: Tags, tone: 'is-green' },
        { label: 'Life Classes', value: classOfPolicies.filter((item) => getCategoryName(item).toLowerCase() === 'life').length, note: 'Life category mapping', icon: ShieldCheck, tone: 'is-amber' },
        { label: 'Policy Types', value: new Set(classOfPolicies.map((item) => item.policyType).filter(Boolean)).size, note: 'Configured type variants', icon: ListChecks, tone: 'is-teal' },
        { label: 'Inactive Classes', value: inactiveList.length, note: 'Loaded from inactive list', icon: ArchiveRestore, tone: 'is-indigo' }
    ];

    const resetForm = () => {
        setShowPopup(false);
        setFormData(emptyForm);
        setEditMode(false);
        setCurrentClassId(null);
        setPositionError('');
        setIsPositionUnique(true);
        setIsPositionChecking(false);
    };

    const openAddPopup = async () => {
        resetForm();
        setShowPopup(true);

        try {
            const res = await api.get(`${API_BASE_URL}/api/class-of-policies/next-position-number`);
            setFormData((prev) => ({ ...prev, positionNumber: String(res.data.next || '') }));
        } catch {
            showNotification('Error loading next position number', 'error');
        }
    };

    const handleEditClass = (item) => {
        setFormData({
            positionNumber: String(item.positionNumber || ''),
            originalPositionNumber: Number(item.positionNumber),
            classOfPolicyName: item.classOfPolicyName || '',
            categoryId: getCategoryId(item) || '',
            claimsTemplate: item.claimsTemplate || '',
            policyType: item.policyType || ''
        });
        setCurrentClassId(item._id);
        setEditMode(true);
        setPositionError('');
        setIsPositionUnique(true);
        setShowPopup(true);
    };

    const checkPositionUnique = (value) => {
        if (editMode && Number(value) === Number(formData.originalPositionNumber)) {
            setIsPositionUnique(true);
            setPositionError('');
            setIsPositionChecking(false);
            return;
        }

        setIsPositionChecking(true);
        setPositionError('');

        if (positionCheckTimer.current) clearTimeout(positionCheckTimer.current);

        positionCheckTimer.current = setTimeout(async () => {
            try {
                const res = await api.get(`${API_BASE_URL}/api/class-of-policies/check-position/${Number(value)}`);

                if (res.data.exists) {
                    setIsPositionUnique(false);
                    setPositionError('This position number is already in use.');
                } else {
                    setIsPositionUnique(true);
                    setPositionError('');
                }
            } catch (error) {
                console.error('Error checking position:', error);
            } finally {
                setIsPositionChecking(false);
            }
        }, 500);
    };

    const handleInputChange = (field, value) => {
        if (field === 'positionNumber') {
            if (!/^\d*$/.test(value)) return;
            if (Number(value) > 99) return;

            setFormData((prev) => ({ ...prev, positionNumber: value }));

            if (!value) {
                setPositionError('Position number is required.');
                setIsPositionUnique(false);
                return;
            }

            setPositionError('');
            setIsPositionUnique(true);
            checkPositionUnique(value);
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [field]: value,
            ...(field === 'categoryId' && categories.find((cat) => cat._id === value)?.categoryName?.toLowerCase() !== 'life'
                ? { policyType: '' }
                : {})
        }));
    };

    const selectedCategoryName = categories.find((cat) => cat._id === formData.categoryId)?.categoryName || '';
    const showPolicyTypeField = selectedCategoryName.toLowerCase() === 'life';
    const isSubmitDisabled = Boolean(
        !formData.positionNumber ||
        !formData.classOfPolicyName.trim() ||
        !formData.categoryId ||
        !formData.claimsTemplate.trim() ||
        !isPositionUnique ||
        positionError ||
        isPositionChecking ||
        (showPolicyTypeField && !formData.policyType)
    );

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitDisabled) {
            showNotification('Please complete all required fields correctly', 'error');
            return;
        }

        const payload = {
            positionNumber: Number(formData.positionNumber),
            classOfPolicyName: formData.classOfPolicyName,
            categoryId: formData.categoryId,
            claimsTemplate: formData.claimsTemplate,
            policyType: formData.policyType
        };

        try {
            if (editMode) {
                const res = await api.put(`${API_BASE_URL}/api/class-of-policies/${currentClassId}`, payload);
                setClassOfPolicies((prev) =>
                    prev.map((item) => (item._id === currentClassId ? res.data.classOfPolicy : item))
                );
                showNotification(res.data.message || 'Updated successfully', 'success');
            } else {
                const res = await api.post(`${API_BASE_URL}/api/class-of-policies`, payload);
                setClassOfPolicies((prev) => [...prev, res.data.classOfPolicy]);
                showNotification(res.data.message || 'Added successfully', 'success');
            }

            resetForm();
        } catch (error) {
            const msg = error.response?.data?.message || 'Error saving record';
            showNotification(msg, 'error');
        }
    };

    const confirmDelete = async () => {
        try {
            await api.put(`${API_BASE_URL}/api/class-of-policies/disable/${classToDelete}`);
            setClassOfPolicies((prev) => prev.filter((cls) => cls._id !== classToDelete));
            showNotification('Class of Policy deactivated successfully', 'success');
        } catch (error) {
            const msg = error.response?.data?.message || 'Error deactivating';
            showNotification(msg, 'error');
        } finally {
            setShowDeleteConfirm(false);
            setClassToDelete(null);
        }
    };

    const openInactivePopup = async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/class-of-policies/inactive/list`);
            setInactiveList(res.data || []);
            setInactiveSearchText('');
            setInactiveCategoryFilter('');
            setSortField('positionNumber');
            setSortOrder('asc');
            setInactivePage(1);
            setShowInactivePopup(true);
        } catch {
            showNotification('Error loading inactive list', 'error');
        }
    };

    const activateClass = async (id) => {
        try {
            await api.put(`${API_BASE_URL}/api/class-of-policies/activate/${id}`);
            await loadActiveClasses();
            setInactiveList((prev) => prev.filter((item) => item._id !== id));
            showNotification('Class activated successfully', 'success');
        } catch {
            showNotification('Error activating class', 'error');
        }
    };

    const restoreAll = async () => {
        try {
            await api.put(`${API_BASE_URL}/api/class-of-policies/restore-all`);
            await loadActiveClasses();
            setInactiveList([]);
            showNotification('All inactive classes restored successfully', 'success');
        } catch {
            showNotification('Error restoring all', 'error');
        }
    };

    const getExportRows = (rows = filteredActive) =>
        rows.map((item, index) => ({
            'S.No': index + 1,
            'Position Number': item.positionNumber || '',
            'Class of Policy Name': item.classOfPolicyName || '',
            Category: getCategoryName(item),
            'Policy Type': item.policyType || '',
            'Claims Template': item.claimsTemplate || ''
        }));

    const handleEmptyExport = () => {
        if (!filteredActive.length) {
            showNotification('No class of policy records available to download', 'error');
            return true;
        }
        return false;
    };

    const exportClassesExcel = () => {
        if (handleEmptyExport()) return;
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Class of Policy');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'class-of-policy-master.xlsx');
        setShowExportMenu(false);
    };

    const exportClassesCsv = () => {
        if (handleEmptyExport()) return;
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'class-of-policy-master.csv');
        setShowExportMenu(false);
    };

    const escapeHtml = (value) =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

    const exportClassesPdf = () => {
        if (handleEmptyExport()) return;
        const rows = getExportRows();
        const printWindow = window.open('', '_blank', 'noopener,noreferrer');
        if (!printWindow) {
            showNotification('Please allow popups to export PDF.', 'error');
            return;
        }

        const tableRows = rows.map((row) => `
            <tr>
                <td>${escapeHtml(row['S.No'])}</td>
                <td>${escapeHtml(row['Position Number'])}</td>
                <td>${escapeHtml(row['Class of Policy Name'])}</td>
                <td>${escapeHtml(row.Category)}</td>
                <td>${escapeHtml(row['Policy Type'])}</td>
                <td>${escapeHtml(row['Claims Template'])}</td>
            </tr>
        `).join('');

        printWindow.document.write(`
            <!doctype html>
            <html>
              <head>
                <title>Class of Policy Export</title>
                <style>
                  body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
                  h1 { margin: 0; font-size: 22px; }
                  p { margin: 6px 0 18px; color: #667085; font-size: 12px; }
                  table { width: 100%; border-collapse: collapse; font-size: 11px; }
                  th, td { border: 1px solid #d7e0ea; padding: 8px; text-align: left; vertical-align: top; }
                  th { background: #f1f5f9; text-transform: uppercase; font-size: 10px; }
                  tr:nth-child(even) td { background: #fbfdff; }
                  @media print { body { margin: 12mm; } }
                </style>
              </head>
              <body>
                <h1>Class of Policy Master</h1>
                <p>${rows.length} class record(s) exported on ${new Date().toLocaleString('en-IN')}</p>
                <table>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Position</th>
                      <th>Class Name</th>
                      <th>Category</th>
                      <th>Policy Type</th>
                      <th>Claims Template</th>
                    </tr>
                  </thead>
                  <tbody>${tableRows || '<tr><td colspan="6">No class records found.</td></tr>'}</tbody>
                </table>
                <script>
                  window.onload = function () {
                    window.print();
                  };
                </script>
              </body>
            </html>
        `);
        printWindow.document.close();
        setShowExportMenu(false);
    };

    if (checkingAuth) {
        return <div className="class-policy-loading-screen">Loading...</div>;
    }

    if (!authenticated) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="class-policy-master-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`class-policy-content-shell ${collapsed ? 'collapsed' : ''}`}>
                <div className="class-policy-sticky-header">
                    <Header />
                </div>

                <main className="class-policy-scroll-area">
                    <section className="class-policy-content">
                        <div className="class-policy-page-hero">
                            <div>
                                <span className="class-policy-page-eyebrow">System Settings / Product Master</span>
                                <h1>Class of Policy</h1>
                                <p>Manage product class ordering, category mapping, claims templates, and life policy type configuration.</p>
                            </div>
                            <div className="class-policy-hero-stats">
                                <span>Active Classes</span>
                                <strong>{classOfPolicies.length}</strong>
                                <small>{filteredActive.length} matching current filters</small>
                            </div>
                        </div>

                        <div className="class-policy-summary-grid">
                            {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
                                <article className={`class-policy-summary-card ${tone}`} key={label}>
                                    <Icon size={22} />
                                    <span>{label}</span>
                                    <strong>{value}</strong>
                                    <small>{note}</small>
                                </article>
                            ))}
                        </div>

                        <section className="class-policy-filter-card">
                            <div className="class-policy-filter-header">
                                <div>
                                    <h2>Search & Filters</h2>
                                    <p>Find classes by position number, class name, category, policy type, or claims template.</p>
                                </div>
                                <span className="class-policy-result-pill">{filteredActive.length} results</span>
                            </div>

                            <div className="class-policy-filter-grid">
                                <label className="class-policy-search-field">
                                    <span>Search Classes</span>
                                    <div>
                                        <Search size={17} />
                                        <input
                                            type="text"
                                            placeholder="Position, class name, category, template"
                                            value={activeSearchText}
                                            onChange={(e) => setActiveSearchText(e.target.value)}
                                        />
                                    </div>
                                </label>

                                <label>
                                    <span>Category</span>
                                    <select value={activeCategoryFilter} onChange={(e) => setActiveCategoryFilter(e.target.value)}>
                                        <option value="">All Categories</option>
                                        {categories.map((cat) => (
                                            <option key={cat._id} value={cat._id}>
                                                {cat.categoryName}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label>
                                    <span>Policy Type</span>
                                    <select value={activePolicyTypeFilter} onChange={(e) => setActivePolicyTypeFilter(e.target.value)}>
                                        <option value="">All Types</option>
                                        <option value="Linked">Linked</option>
                                        <option value="Non-Linked">Non-Linked</option>
                                        <option value="Riders">Riders</option>
                                    </select>
                                </label>
                            </div>

                            <div className="class-policy-action-row">
                                {hasPrivilege(classPrivileges.add) && <button className="class-policy-btn class-policy-btn-primary" onClick={openAddPopup}>
                                    <Plus size={16} /> Add Class
                                </button>}

                                {hasPrivilege(classPrivileges.viewInactive) && <button className="class-policy-btn class-policy-btn-ghost" onClick={openInactivePopup}>
                                    <ArchiveRestore size={16} /> Inactive List
                                </button>}

                                <button
                                    className="class-policy-btn class-policy-btn-ghost"
                                    onClick={() => {
                                        setActiveSearchText('');
                                        setActiveCategoryFilter('');
                                        setActivePolicyTypeFilter('');
                                    }}
                                >
                                    <RefreshCw size={16} /> Reset
                                </button>

                                {hasPrivilege(classPrivileges.download) && <div className="class-policy-export-menu">
                                    <button
                                        className="class-policy-btn class-policy-btn-export"
                                        onClick={() => setShowExportMenu((prev) => !prev)}
                                    >
                                        <Download size={16} /> Download <ChevronDown size={15} />
                                    </button>
                                    {showExportMenu && (
                                        <div className="class-policy-export-dropdown">
                                            <button onClick={exportClassesCsv}>CSV</button>
                                            <button onClick={exportClassesExcel}>Excel</button>
                                            <button onClick={exportClassesPdf}>PDF</button>
                                        </div>
                                    )}
                                </div>}
                            </div>
                        </section>

                        <section className="class-policy-table-card">
                            <div className="class-policy-table-toolbar">
                                <div>
                                    <h2>Class Records</h2>
                                    <p>Ordered class setup with quick edits, category mapping, and claims template visibility.</p>
                                </div>
                                <span className="class-policy-result-pill">{activePolicies.length} shown</span>
                            </div>

                            <div className="class-policy-table-scroll">
                                <table className="class-policy-table">
                                    <thead>
                                        <tr>
                                            <th className="class-policy-sticky-name">Class of Policy</th>
                                            <th>Position</th>
                                            <th>Category</th>
                                            <th>Policy Type</th>
                                            <th>Claims Template</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activePolicies.length ? (
                                            activePolicies.map((policy) => (
                                                <tr key={policy._id}>
                                                    <td className="class-policy-sticky-name" data-label="Class of Policy">
                                                        <div className="class-policy-name-cell">
                                                            <span className="class-policy-avatar">
                                                                <FolderTree size={17} />
                                                            </span>
                                                            <span>
                                                                <strong>{policy.classOfPolicyName}</strong>
                                                                <small>{getCategoryName(policy)}</small>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td data-label="Position">
                                                        <span className="class-policy-position-chip">
                                                            <Hash size={13} /> {policy.positionNumber}
                                                        </span>
                                                    </td>
                                                    <td data-label="Category">
                                                        <span className="class-policy-chip">{getCategoryName(policy)}</span>
                                                    </td>
                                                    <td data-label="Policy Type">{policy.policyType || '-'}</td>
                                                    <td data-label="Claims Template">{policy.claimsTemplate || '-'}</td>
                                                    <td data-label="Actions">
                                                        <div className="class-policy-action-icons">
                                                            {hasPrivilege(classPrivileges.edit) && <button className="class-policy-icon-btn edit-btn" onClick={() => handleEditClass(policy)} title="Edit Class" aria-label={`Edit ${policy.classOfPolicyName}`}>
                                                                <Edit size={18} />
                                                            </button>}
                                                            {hasPrivilege(classPrivileges.deactivate) && <button
                                                                className="class-policy-icon-btn delete-btn"
                                                                onClick={() => {
                                                                    setClassToDelete(policy._id);
                                                                    setShowDeleteConfirm(true);
                                                                }}
                                                                title="Deactivate Class"
                                                                aria-label={`Deactivate ${policy.classOfPolicyName}`}
                                                            >
                                                                <Ban size={18} />
                                                            </button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6">
                                                    <div className="class-policy-empty-state">
                                                        <FolderTree size={42} />
                                                        <h3>No Classes Found</h3>
                                                        <p>Try changing the filters or add a new class of policy.</p>
                                                        {hasPrivilege(classPrivileges.add) && <button className="class-policy-btn class-policy-btn-primary" onClick={openAddPopup}>
                                                            <Plus size={16} /> Add Class
                                                        </button>}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <div className="class-policy-pagination-row">
                            <div className="class-policy-pagination-meta">
                                <span>
                                    Showing {filteredActive.length ? activeFirst + 1 : 0}-{Math.min(activeLast, filteredActive.length)} of {filteredActive.length}
                                </span>
                                <small>Filtered class records</small>
                            </div>

                            <div className="class-policy-pagination-controls">
                                <Pagination
                                    totalItems={filteredActive.length}
                                    itemsPerPage={activeItemsPerPage}
                                    currentPage={activePage}
                                    onPageChange={setActivePage}
                                />
                            </div>

                            <div className="class-policy-items-per-page">
                                <label>Items per page:</label>
                                <select
                                    value={activeItemsPerPage}
                                    onChange={(e) => {
                                        setActiveItemsPerPage(Number(e.target.value));
                                        setActivePage(1);
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>

                        {showDeleteConfirm && (
                            <div className="class-policy-confirmation-modal-overlay">
                                <div className="class-policy-confirmation-modal-content">
                                    <h3>Confirm Deactivation</h3>
                                    <p>Are you sure you want to deactivate this Class of Policy?</p>
                                    <div className="class-policy-confirmation-modal-actions">
                                        <button className="class-policy-confirm-btn" onClick={confirmDelete}>Yes</button>
                                        <button
                                            className="class-policy-cancel-btn"
                                            onClick={() => {
                                                setShowDeleteConfirm(false);
                                                setClassToDelete(null);
                                            }}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showPopup && (
                            <div className="class-policy-popup-overlay">
                                <div className="class-policy-popup">
                                    <button className="class-policy-popup-close" onClick={resetForm} aria-label="Close class form">
                                        <X size={18} />
                                    </button>
                                    <h2>{editMode ? 'Edit Class of Policy' : 'Add Class of Policy'}</h2>
                                    <p className="class-policy-popup-subtitle">Set class order, category, claims template, and life policy type when required.</p>

                                    <form onSubmit={handleFormSubmit} className="class-policy-form">
                                        <label>
                                            <span>Position Number</span>
                                            <input
                                                type="text"
                                                placeholder="01"
                                                value={formData.positionNumber}
                                                maxLength={2}
                                                onChange={(e) => handleInputChange('positionNumber', e.target.value)}
                                                required
                                            />
                                            {isPositionChecking && <small className="class-policy-info-text">Checking position number...</small>}
                                            {positionError && <small className="class-policy-error-text">{positionError}</small>}
                                        </label>

                                        <label>
                                            <span>Class of Policy Name</span>
                                            <input
                                                type="text"
                                                placeholder="Enter class name"
                                                value={formData.classOfPolicyName}
                                                onChange={(e) => handleInputChange('classOfPolicyName', e.target.value)}
                                                required
                                            />
                                        </label>

                                        <label>
                                            <span>Category</span>
                                            <select
                                                value={formData.categoryId}
                                                onChange={(e) => handleInputChange('categoryId', e.target.value)}
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat._id} value={cat._id}>
                                                        {cat.categoryName}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        {showPolicyTypeField && (
                                            <label>
                                                <span>Policy Type</span>
                                                <select
                                                    value={formData.policyType}
                                                    onChange={(e) => handleInputChange('policyType', e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select Policy Type</option>
                                                    <option value="Linked">Linked</option>
                                                    <option value="Non-Linked">Non-Linked</option>
                                                    <option value="Riders">Riders</option>
                                                </select>
                                            </label>
                                        )}

                                        <label className="class-policy-form-full">
                                            <span>Claims Template</span>
                                            <input
                                                type="text"
                                                placeholder="Choose claims template"
                                                value={formData.claimsTemplate}
                                                onChange={(e) => handleInputChange('claimsTemplate', e.target.value)}
                                                required
                                            />
                                        </label>

                                        <div className="class-policy-popup-actions">
                                            <button type="submit" className="class-policy-submit-btn" disabled={isSubmitDisabled}>
                                                {editMode ? 'Update Class' : 'Save Class'}
                                            </button>
                                            <button type="button" className="class-policy-popup-cancel-btn" onClick={resetForm}>
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {showInactivePopup && (
                            <div className="class-policy-popup-overlay">
                                <div className="class-policy-popup large">
                                    <button className="class-policy-popup-close" onClick={() => setShowInactivePopup(false)} aria-label="Close inactive classes">
                                        <X size={18} />
                                    </button>
                                    <h2>Inactive Class of Policy</h2>
                                    <p className="class-policy-popup-subtitle">Review deactivated classes and restore them when required.</p>

                                    <div className="class-policy-inactive-filters">
                                        <input
                                            type="text"
                                            placeholder="Search inactive classes"
                                            value={inactiveSearchText}
                                            onChange={(e) => setInactiveSearchText(e.target.value)}
                                        />

                                        <select value={inactiveCategoryFilter} onChange={(e) => setInactiveCategoryFilter(e.target.value)}>
                                            <option value="">All Categories</option>
                                            {categories.map((cat) => (
                                                <option key={cat._id} value={cat._id}>
                                                    {cat.categoryName}
                                                </option>
                                            ))}
                                        </select>

                                        <select value={sortField} onChange={(e) => setSortField(e.target.value)}>
                                            <option value="positionNumber">Position Number</option>
                                            <option value="classOfPolicyName">Class Name</option>
                                            <option value="categoryName">Category</option>
                                            <option value="claimsTemplate">Claims Template</option>
                                        </select>

                                        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                                            <option value="asc">ASC</option>
                                            <option value="desc">DESC</option>
                                        </select>

                                        <button className="class-policy-restore-all-btn" onClick={restoreAll}>
                                            <ArchiveRestore size={15} /> Restore All
                                        </button>
                                    </div>

                                    <div className="class-policy-inactive-table-scroll">
                                        <table className="class-policy-inactive-table">
                                            <thead>
                                                <tr>
                                                    <th>Position</th>
                                                    <th>Class Name</th>
                                                    <th>Category</th>
                                                    <th>Claims Template</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {inactiveItems.length ? (
                                                    inactiveItems.map((item) => (
                                                        <tr key={item._id}>
                                                            <td>{item.positionNumber}</td>
                                                            <td>{item.classOfPolicyName}</td>
                                                            <td>{getCategoryName(item)}</td>
                                                            <td>{item.claimsTemplate || '-'}</td>
                                                            <td>
                                                                <button className="class-policy-activate-btn" onClick={() => activateClass(item._id)}>
                                                                    Restore
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5">No inactive records found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="class-policy-modal-pagination-row">
                                        <Pagination
                                            totalItems={filteredInactive.length}
                                            itemsPerPage={inactiveItemsPerPage}
                                            currentPage={inactivePage}
                                            onPageChange={setInactivePage}
                                        />

                                        <div className="class-policy-items-per-page">
                                            <label>Items per page:</label>
                                            <select
                                                value={inactiveItemsPerPage}
                                                onChange={(e) => {
                                                    setInactiveItemsPerPage(Number(e.target.value));
                                                    setInactivePage(1);
                                                }}
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

export default ClassofPolicyMaster;
